import React from 'react'
import { Head, router, usePage } from '@inertiajs/react'
import { FunnelExecution, FunnelStepData } from '@/types'

// Dynamic import for components from the ds folder
const getComponent = async (stepName: string) => {
  try {
    // Convert step name to kebab-case for filename matching
    const fileName = stepName.replace(/([A-Z])/g, '-$1').toLowerCase()
    const module = await import(`@/components/funnels/ds/${fileName}.tsx`)
    return module.default
  } catch (error) {
    console.error(`Component not found for step: ${stepName}`, error)
    return null
  }
}

export default function Show() {
  const funnelStepData = usePage().props.step_data as FunnelStepData
  const [Component, setComponent] = React.useState<React.ComponentType<any> | null>(null)
  const [loading, setLoading] = React.useState(true)
  const funnelExecution = usePage().props.funnel_execution as FunnelExecution

  React.useEffect(() => {
    const loadComponent = async () => {
      setLoading(true)
      const DynamicComponent = await getComponent(funnelStepData.current_step)
      setComponent(() => DynamicComponent)
      setLoading(false)
    }

    loadComponent()
  }, [funnelStepData.current_step])

  const handleNext = (payload: Record<string, any>) => {
    router.patch(window.location.pathname, payload)
  }

  if (loading) {
    return (
      <>
        <Head title={`${funnelStepData.current_step.replace(/-/g, ' ').replace(/\b\w/g, char => char.toUpperCase())}`} />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading component...</p>
          </div>
        </div>
      </>
    )
  }

  if (!Component) {
    return (
      <>
        <Head title="Component Not Found" />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Component Not Found</h1>
            <p className="text-gray-600 mb-4">
              No component found for step: <code className="bg-gray-100 px-2 py-1 rounded">{funnelStepData.current_step}</code>
            </p>
            <div className="text-sm text-gray-500">
              <p>Expected file: <code className="bg-gray-100 px-2 py-1 rounded">components/funnels/ds/{funnelStepData.current_step.replace(/([A-Z])/g, '-$1').toLowerCase()}.tsx</code></p>
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <Head title={`${funnelStepData.current_step.replace(/-/g, ' ').replace(/\b\w/g, char => char.toUpperCase())}`} />
      <Component 
        onNext={handleNext} 
        card_string={funnelExecution.stripe_card_string} 
        opted_to_pro={funnelExecution.opted_to_pro}
        funnel_id={funnelExecution.funnel_id}
      />
    </>
  )
}
